import { Link } from "@remix-run/react";
import { Card } from "~/components/ui/Card";

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  category: string;
  tags: string[];
  featuredImageUrl?: string | null;
  publishedAt?: string | null;
  createdAt: string;
}

interface ArticleCardProps {
  article: Article;
  categoryLabel: string;
}

export function ArticleCard({ article, categoryLabel }: ArticleCardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Card className="h-full flex flex-col group hover:shadow-lg transition-all duration-200">
      <Link to={`/articles/${article.slug}`} className="block">
        {/* Image Section */}
        <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-t-lg overflow-hidden relative">
          {article.featuredImageUrl ? (
            <img
              src={article.featuredImageUrl}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
              <svg
                className="w-12 h-12 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          )}

          {/* Category Badge Overlay */}
          <div className="absolute top-3 left-3">
            <span className="inline-block px-2 py-1 text-xs font-medium bg-white/90 backdrop-blur-sm text-blue-800 rounded shadow-sm">
              {categoryLabel}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-6 flex-1 flex flex-col">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {article.title}
          </h3>

          {article.excerpt && (
            <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 flex-1 text-sm leading-relaxed">
              {article.excerpt}
            </p>
          )}

          {/* Meta Info */}
          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
            <span>{formatDate(article.publishedAt || article.createdAt)}</span>
            {article.tags.length > 0 && (
              <div className="flex gap-1">
                {article.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="text-xs text-gray-400">
                    #{tag}
                  </span>
                ))}
                {article.tags.length > 2 && (
                  <span className="text-xs text-gray-400">
                    +{article.tags.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Read More Link */}
          <div className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium text-sm group-hover:translate-x-1 transition-transform">
            자세히 보기
            <svg
              className="ml-1 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </Link>
    </Card>
  );
}
